import { Link, useParams } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useEffect } from "react";
import { SEO, breadcrumbSchema } from "../components/SEO";
import { PageHero } from "../components/PageHero";
import { Reveal, ClipImage } from "../components/Reveal";
import { OrangeCta } from "../components/OrangeCta";
import { PROJECTS } from "../data/work";
import { SERVICES } from "../data/services";
import { INDUSTRY_NAME_PAGE } from "../data/industries";
import { IMG } from "../data/assets";
import { SITE } from "../data/site";
import { track } from "../lib/analytics";
import NotFound from "./NotFound";

// Region → permanent location page (matched by city name inside the region).
const LOCATION_PAGE = { Mumbai: "mumbai", Bengaluru: "bengaluru", Goa: "goa", Pune: "pune", Hyderabad: "hyderabad" };

const STAGES = [
  { key: "challenge", label: "Challenge" },
  { key: "inspection", label: "Inspection" },
  { key: "execution", label: "Execution" },
  { key: "evidence", label: "Evidence" },
  { key: "outcome", label: "Outcome" },
];

// "Project at a glance" — renders only fields that exist in the data
// (base fields + optional p.glance metrics). New metrics appear automatically
// once DCC supplies them; nothing is fabricated to fill the grid.
function GLANCE_ROWS(p, service, industrySlug, locationSlug) {
  const g = p.glance || {};
  const rows = [];
  if (p.industry) rows.push({ label: "Industry", value: p.industry, to: industrySlug ? `/industries/${industrySlug}` : "/industries", testid: "case-industry-link" });
  if (p.region) rows.push({ label: "Location", value: p.region, to: locationSlug ? `/locations/${locationSlug}` : "/locations", testid: "case-location-link" });
  if (service) rows.push({ label: "Service", value: service.name, to: `/services/${service.slug}`, testid: "case-service-link" });
  const OPTIONAL = [
    ["systemSize", "System size"], ["assets", "Duct length / assets"], ["accessPoints", "Access points"],
    ["teamSize", "Team size"], ["window", "Working window"], ["shifts", "Number of shifts"],
    ["duration", "Execution duration"], ["downtime", "Operational downtime"],
    ["contamination", "Contamination observed"], ["verification", "Verification method"],
    ["evidenceDelivered", "Evidence delivered"], ["metric", "Measurable outcome"],
  ];
  OPTIONAL.forEach(([k, label]) => { if (g[k]) rows.push({ label, value: g[k] }); });
  return rows;
}


// Permanent case-study pages — content from the approved project stories only.
export default function CaseStudy() {
  const { slug } = useParams();
  const p = PROJECTS.find((x) => x.id === slug);

  useEffect(() => {
    if (p) track("case_study_open", { case_study: p.id, industry: p.industry });
  }, [p]);

  if (!p) return <NotFound />;
  const service = SERVICES.find((s) => s.id === p.service);
  const industrySlug = INDUSTRY_NAME_PAGE[p.industry];
  const locationSlug = Object.entries(LOCATION_PAGE).find(([city]) => p.region.includes(city))?.[1];

  const jsonLd = [
    breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Our Work", path: "/our-work" },
      { name: p.title, path: `/our-work/${p.id}` },
    ]),
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: p.title,
      description: p.challenge,
      image: `${SITE.url}${IMG[p.image].src}`,
      author: { "@type": "Organization", name: SITE.name, url: SITE.url },
      publisher: { "@type": "Organization", name: SITE.name, logo: { "@type": "ImageObject", url: `${SITE.url}/assets/dcc-logo.webp` } },
      mainEntityOfPage: `${SITE.url}/our-work/${p.id}`,
      about: service ? service.name : undefined,
      contentLocation: { "@type": "Place", name: p.region },
    },
  ];

  return (
    <>
      <SEO title={p.seoTitle || `${p.title} | DCC Case Study`} description={p.challenge} path={`/our-work/${p.id}`} type="article" jsonLd={jsonLd} image={IMG[p.image].src} />
      <PageHero
        eyebrow={`Case study · ${p.industry} · ${p.region}`}
        lines={[p.title]}
        copy="Client identity withheld where confidentiality applies. The spine is always the same: Challenge → Inspection → Execution → Evidence → Outcome."
      />

      <section className="bg-offwhite py-20 md:py-28">
        <div className="container-x grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <ClipImage src={IMG[p.image].src} alt={IMG[p.image].alt} ratio="aspect-[4/3]" className="border border-zinc-200" />
            <div className="mt-8 border border-zinc-200 bg-white" data-testid="project-glance">
              <p className="eyebrow border-b border-zinc-200 px-5 py-3">Project at a glance</p>
              <div className="px-5">
                {GLANCE_ROWS(p, service, industrySlug, locationSlug).map((r) => (
                  <div key={r.label} className="flex items-center justify-between gap-4 border-b border-zinc-100 py-4 last:border-b-0">
                    <span className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">{r.label}</span>
                    {r.to ? (
                      <Link to={r.to} data-testid={r.testid} className="text-right font-mono text-[11px] uppercase tracking-[0.15em] text-dcc hover:underline">{r.value}</Link>
                    ) : (
                      <span className="text-right font-mono text-[11px] uppercase tracking-[0.15em] text-charcoal">{r.value}</span>
                    )}
                  </div>
                ))}
                <div className="flex items-center justify-between gap-4 border-t border-zinc-200 py-4">
                  <span className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">Next step</span>
                  <Link to={`/request-survey?service=${service ? service.slug : ""}&source=case-study`} data-testid="case-survey-cta" className="text-right font-mono text-xs uppercase tracking-[0.15em] text-dcc hover:underline">Request a free site survey</Link>
                </div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7">
            {STAGES.map((s, i) => (
              <Reveal key={s.key} delay={i * 0.05}>
                <div className="border-b border-zinc-200 py-7">
                  <h2 className="font-display text-xl font-semibold uppercase tracking-tight text-charcoal">
                    <span className="mr-3 font-mono text-xs text-dcc-strong">{String(i + 1).padStart(2, "0")}</span>{s.label}
                  </h2>
                  <p className="mt-3 max-w-2xl text-sm leading-relaxed text-zinc-700 md:text-base">{p[s.key]}</p>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.1}>
              <p className="mt-8 font-mono text-[11px] leading-relaxed text-zinc-500">
                // Field photography from the live project. Full evidence dossiers are shared during the survey stage, subject to client confidentiality.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <OrangeCta
        lines={["WANT THIS EVIDENCE", "FOR YOUR FACILITY?"]}
        copy="Every DCC engagement closes with the same documented spine. Start with a free technical survey of your system."
      />
    </>
  );
}
